import { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, message, Card, Checkbox } from 'antd'
import { Formik } from 'formik'
import humps from 'humps'
import { useNavigate, useParams } from 'react-router-dom'
import startCase from 'lodash/startCase'
import get from 'lodash/get'
import noop from 'lodash/noop'
import { createUseStyles } from 'react-jss'
import { reportShowPath } from '../../lib/reports'

import { formElementPropsGenerator } from '../../lib/forms'
import { SubmitButton } from '../Forms'
import api from '../../api'
import useQueryParams from '../../hooks/useQueryParams'
import QuestionForm from './QuestionForm'
import FormItem from './FormItem'

import {
  formatInitialValues,
  formatSavedValues,
  getLabels,
  getPlaceholders,
  buildValidationSchema,
} from './form-utils'
import FormItems from '../Common/FormItems'

function filterCycle30Fields(formSections) {
  return formSections.filter(
    section => !section.restrictedTo?.includes('cycle_30'),
  )
}

const useStyles = createUseStyles({
  sectionCard: {
    marginTop: '16px',
    marginBottom: '16px',
  },
})

export default function ReportForm({
  report,
  rawReport = null,
  showIssuesForm = true,
  issuesFormFields = {
    errorFields: [],
    observations: '',
  },
  onReportField = noop,
}) {
  const navigate = useNavigate()
  const { id } = useParams()
  const classes = useStyles()
  const [answeredQuestion, setAnsweredQuestion] = useState(false)
  const [filterFields, setFilterFields] = useState(
    () =>
      report.filteredFields ||
      (report.hasLongCycleAndDeviceWithFullLength &&
        report.device?.icmActive === false),
  )
  const [query, , getPathWithQuery] = useQueryParams({})
  const [isProcessing, setIsProcessing] = useState(false)

  const {
    formSections: initialFormSections,
    fieldValues,
    device,
    additionalQuestion,
    hasFile,
    version,
    hasLongCycleAndDeviceWithFullLength,
    suggestions,
    alertDates = [],
    biobridgeReport,
    critical,
  } = report
  const showQuestionForm = !hasFile && !answeredQuestion && additionalQuestion
  const formSections = filterFields
    ? filterCycle30Fields(initialFormSections)
    : initialFormSections

  const handleEdit = useCallback(
    async values => {
      const { critical: criticalValue, ...fieldFormValues } = values
      const reportValues = formatSavedValues(fieldFormValues, formSections)
      try {
        if (issuesFormFields.errorFields.length > 0) {
          // Send issues form fields
          await api.transmissions.reportExtractionError(
            rawReport?.pdfExtraction.id,
            issuesFormFields,
          )
        }
        if (report.version === 'v2' && !report.biobridgeReport) {
          await api.remoteChecks.createBiobridgeReport(id, {
            reportValues,
          })
        } else {
          await api.reports.updateReportForm(
            id,
            { reportValues, critical: criticalValue },
            version,
          )
        }

        navigate(getPathWithQuery(reportShowPath(report), query))
        message.success('BioBridge report has been edited')
      } catch (e) {
        message.error(
          `There was an error editing a BioBridge report: ${e.message}`,
        )
      }
    },
    [
      formSections,
      issuesFormFields,
      navigate,
      report,
      getPathWithQuery,
      query,
      rawReport,
      id,
      version,
    ],
  )

  const handleQuestion = useCallback(
    async ({ question }) => {
      try {
        if (question === 'yes') {
          setAnsweredQuestion(true)
          return
        }

        if (question === 'no') {
          const feedbackKey = 'icm-deactivation'
          setIsProcessing(true)
          message.loading({
            content: 'Updating report…',
            key: feedbackKey,
            duration: 0,
          })
          try {
            // Keep the "Updating report…" status visible for a beat even
            // when the request is fast, so the user sees the feedback.
            await Promise.all([
              api.remoteChecks.deactivateIcm(id),
              new Promise(resolve => {
                setTimeout(resolve, 700)
              }),
            ])
          } catch (e) {
            message.error({
              content: `Error turning off ICM: ${e.message}`,
              key: feedbackKey,
            })
            return
          } finally {
            setIsProcessing(false)
          }

          if (hasLongCycleAndDeviceWithFullLength) {
            // Long cycle keeps a 90-day device report — hide the ICM fields
            // and stay on the form.
            setFilterFields(true)
            message.success({
              content: 'ICM removed from billing — report updated',
              key: feedbackKey,
            })
            setAnsweredQuestion(true)
          } else {
            // Short cycle is ICM-only — the report was deleted on the
            // backend, so go back to the remote-checks queue.
            message.success({
              content: 'ICM turned off — report removed',
              key: feedbackKey,
            })
            navigate(-1)
          }
        }
      } catch (e) {
        message.error(`There was an error handling the response: ${e.message}`)
      }
    },
    [id, hasLongCycleAndDeviceWithFullLength, navigate],
  )

  if (showQuestionForm) {
    return (
      <QuestionForm
        additionalQuestion={additionalQuestion}
        handleQuestion={handleQuestion}
        disabled={isProcessing}
      />
    )
  }

  const validationSchema = buildValidationSchema(formSections)
  const formType = humps.camelize(
    get(device, 'deviceModel.deviceType.formType'),
  )
  const propsFor = formElementPropsGenerator({
    formName: 'report-edit-form',
    labels: { ...getLabels(formSections), critical: 'Requires Attention' },
    placeholders: getPlaceholders(formSections),
  })

  // Helper to append alert dates to thoracic impedance summary if BioBridge report not generated
  const appendAlertDatesToInitialValues = initialVals => {
    // Only populate if BioBridge report hasn't been generated yet
    if (biobridgeReport) return initialVals

    // Find the thoracic impedance summary fields
    const thoracicFields = formSections
      .flatMap(section => section.fields)
      .filter(field => {
        const name = field.itemName?.toLowerCase()
        return name?.includes('summary')
      })

    if (thoracicFields.length === 0) return initialVals

    // Process each matching field
    const updatedValues = { ...initialVals }

    thoracicFields.forEach(thoracicField => {
      const fieldValue = initialVals[thoracicField.itemId]

      // Only append if field is empty
      if (!fieldValue || fieldValue.trim() === '') {
        const alertLine =
          alertDates.length > 0
            ? `Alert(s) submitted on ${alertDates.join(', ')}.`
            : 'No alerts submitted in last 91 days.'

        updatedValues[thoracicField.itemId] = alertLine
      }
    })

    return updatedValues
  }

  const initialFormValues = appendAlertDatesToInitialValues({
    ...formatInitialValues(fieldValues, formSections, formType, rawReport),
    critical: Boolean(critical),
  })

  return (
    <Formik
      onSubmit={handleEdit}
      initialValues={initialFormValues}
      validationSchema={validationSchema}
    >
      {({
        handleSubmit,
        isSubmitting,
        isValid,
        submitCount,
        values,
        setFieldValue,
      }) => (
        <Form onFinish={handleSubmit}>
          {formSections.map(section => (
            <Card
              type="inner"
              title={startCase(section.name)}
              key={section.name}
              className={classes.sectionCard}
              size="small"
            >
              <FormItems>
                {section.fields.map(field => {
                  const isParsed =
                    rawReport?.deviceReportMetadata?.[
                      humps.camelize(field.itemName)
                    ] !== null

                  const key = humps.camelize(field.itemName)
                  return (
                    <div key={field.itemId}>
                      <FormItem
                        propsFor={propsFor}
                        field={field}
                        formType={formType}
                        suggestions={
                          suggestions.find(
                            suggestion =>
                              suggestion.reportItemId === field.itemId,
                          )?.suggestions || []
                        }
                        layout="vertical"
                      />
                      {isParsed &&
                        showIssuesForm &&
                        !report.biobridgeReport && (
                          <Checkbox
                            checked={issuesFormFields.errorFields.includes(key)}
                            onClick={() => onReportField(field)}
                          >
                            Report autofill error
                          </Checkbox>
                        )}
                    </div>
                  )
                })}
              </FormItems>
            </Card>
          ))}
          <Card
            type="inner"
            title="Requires Attention"
            className={classes.sectionCard}
            size="small"
          >
            <Checkbox
              {...propsFor('critical')}
              checked={values.critical}
              onChange={e => setFieldValue('critical', e.target.checked)}
            />
          </Card>
          <SubmitButton
            isSubmitting={isSubmitting}
            isValid={isValid}
            submitCount={submitCount}
            title="Save Report"
          />
        </Form>
      )}
    </Formik>
  )
}

ReportForm.propTypes = {
  report: PropTypes.shape().isRequired,
  rawReport: PropTypes.shape(),
  issuesFormFields: PropTypes.shape(),
  onReportField: PropTypes.func,
  showIssuesForm: PropTypes.bool,
}
